import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PRODUCTION_DOMAIN = 'https://repriscan.com';

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  noIndex?: boolean;
}

/**
 * SEO Head component that dynamically manages:
 * - Canonical URLs (always pointing to repriscan.com)
 * - Robots meta (noindex on lovable.app)
 * - OpenGraph tags with correct og:url
 * - Twitter cards
 */
export function SEOHead({
  title = 'RepriScan - Prédiagnostic de reprise d\'entreprise',
  description = 'RepriScan vous accompagne dans l\'analyse et le prédiagnostic de reprise d\'entreprise. Évaluez rapidement la viabilité d\'une opportunité d\'acquisition.',
  ogImage = `${PRODUCTION_DOMAIN}/og-image.png`,
  noIndex = false,
}: SEOHeadProps) {
  const location = useLocation();
  
  // Determine if we're on the staging domain
  const isLovableApp = typeof window !== 'undefined' && 
    window.location.hostname.includes('lovable.app');
  
  // Build canonical URL (always points to production domain)
  const canonicalUrl = `${PRODUCTION_DOMAIN}${location.pathname === '/' ? '' : location.pathname}`;
  
  // Should this page be noindexed?
  const shouldNoIndex = noIndex || isLovableApp;
  
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Helper to update or create meta tag
    const setMetaTag = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        if (selector.startsWith('meta[property')) {
          const prop = selector.match(/property="([^"]+)"/)?.[1];
          if (prop) element.setAttribute('property', prop);
        } else if (selector.startsWith('meta[name')) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };
    
    // Helper to update or create link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };
    
    // Set canonical link
    setLinkTag('canonical', canonicalUrl);
    
    // Set meta description
    setMetaTag('meta[name="description"]', 'content', description);
    
    // Set robots meta
    if (shouldNoIndex) {
      setMetaTag('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists and we shouldn't have it
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }
    
    // OpenGraph tags
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl);
    setMetaTag('meta[property="og:image"]', 'content', ogImage);
    setMetaTag('meta[property="og:type"]', 'content', 'website');
    setMetaTag('meta[property="og:site_name"]', 'content', 'RepriScan');
    setMetaTag('meta[property="og:locale"]', 'content', 'fr_FR');
    
    // Twitter Card tags
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
    setMetaTag('meta[name="twitter:image"]', 'content', ogImage);
    
    // Cleanup function
    return () => {
      // We don't remove tags on cleanup as they should persist
    };
  }, [title, description, ogImage, canonicalUrl, shouldNoIndex]);
  
  return null; // This component only manages head tags
}

export default SEOHead;
